import React, { useEffect, useState } from 'react';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Badge from '@mui/material/Badge';
import { useTheme } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Campaign';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import PianoSharpIcon from '@mui/icons-material/PianoSharp';
import AdminPanelIcon from '@mui/icons-material/AdminPanelSettings';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';

function BottomNavBar({ userRole }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingLessonCount, setPendingLessonCount] = useState(0);
  const [waitlistUnread, setWaitlistUnread] = useState(0);

  const navItems = [
    { label: '공지사항', value: '/notice', icon: <HomeIcon /> },
    { label: '레슨', value: '/lesson', icon: <PianoSharpIcon /> },
    { label: '연습실', value: '/practice', icon: <LibraryMusicIcon /> }
  ];

  if (userRole === "MEMBER") {
    navItems.push(
      { label: "내일정", value: "/my-schedule", icon: <CalendarIcon /> },
      { label: "내정보", value: "/myinfo", icon: <PersonIcon /> }
    );
  }

  if (userRole === "TEACHER") {
    navItems.push(
      { 
        label: "레슨신청", 
        value: "/lesson-requests", 
        icon: (
          <Badge badgeContent={pendingLessonCount + waitlistUnread} color="error" variant="dot" invisible={pendingLessonCount === 0 && waitlistUnread === 0}>
            <SchoolIcon />
          </Badge>
        )
      },
      { 
        label: "변경사항", 
        value: "/activities", 
        icon: (
          <Badge badgeContent={unreadCount} color="error" variant="dot" invisible={unreadCount === 0}>
            <PriorityHighIcon />
          </Badge>
        )
      }
    );
  }  

  if (userRole === "ADMIN") {
    navItems.push(
      { label: "관리", value: "/admin", icon: <AdminPanelIcon /> },
      { 
        label: "레슨신청", 
        value: "/lesson-requests", 
        icon: (
          <Badge badgeContent={pendingLessonCount + waitlistUnread} color="error" variant="dot" invisible={pendingLessonCount === 0 && waitlistUnread === 0}>
            <SchoolIcon />
          </Badge>
        )
      },
      { 
        label: "변경사항", 
        value: "/activities", 
        icon: (
          <Badge badgeContent={unreadCount} color="error" variant="dot" invisible={unreadCount === 0}>
            <PriorityHighIcon />
          </Badge>
        )
      }
    );
  }

  useEffect(() => {
    if (userRole === 'ADMIN' || userRole === 'TEACHER') {
      const fetchUnreadCount = async () => {
        try {
          const res = await api.get('/activities/unread-count');
          setUnreadCount(res.data.unreadCount);
        } catch (err) {
          console.error('미확인 활동 개수 조회 실패', err);
        }
      };
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [userRole]);

  useEffect(() => {
    if (userRole === 'ADMIN' || userRole === 'TEACHER') {
      const fetchPendingLessonCount = async () => {
        try {
          const res = await api.get('/lesson-requests?status=PENDING');
          setPendingLessonCount(res.data.filter(r => r.status === 'PENDING').length);
        } catch (err) {
          console.error('대기 중인 레슨 신청 조회 실패', err);
        }
      };
      fetchPendingLessonCount();
      const interval = setInterval(fetchPendingLessonCount, 60000);
      return () => clearInterval(interval);
    }
  }, [userRole]);

  useEffect(() => {
    if (userRole === 'ADMIN' || userRole === 'TEACHER') {
      const fetchWaitlistUnread = async () => {
        try {
          const res = await api.get('/lesson-waitlist/unread-count');
          setWaitlistUnread(res.data.unreadCount);
        } catch (err) {
          console.error('빈자리 대기 미확인 개수 조회 실패', err);
        }
      };
      fetchWaitlistUnread();
      const interval = setInterval(fetchWaitlistUnread, 60000);
      return () => clearInterval(interval);
    }
  }, [userRole]);

  useEffect(() => {
    if (location.pathname === '/activities' && unreadCount > 0) {
      setUnreadCount(0);
    }
  }, [location.pathname, unreadCount]);

  useEffect(() => {
    if (location.pathname === '/lesson-requests' && pendingLessonCount > 0) {
      setPendingLessonCount(0);
    }
  }, [location.pathname, pendingLessonCount]);

  // 레슨신청 페이지 진입 시 빈자리 대기 읽음 처리 (서버에 마지막 확인 시각 기록)
  useEffect(() => {
    if (location.pathname === '/lesson-requests' && waitlistUnread > 0) {
      setWaitlistUnread(0);
      api.post('/lesson-waitlist/mark-read').catch(err =>
        console.error('빈자리 대기 읽음 처리 실패', err)
      );
    }
  }, [location.pathname, waitlistUnread]);

  const [value, setValue] = React.useState(location.pathname);

  React.useEffect(() => {
    setValue(location.pathname);
  }, [location.pathname]);

  return (
    <BottomNavigation
      value={value}
      showLabels
      onChange={(_, newValue) => {
        setValue(newValue);
        navigate(newValue);
      }}
      sx={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        // 하단 내비게이션(제스처바/3버튼) 영역만큼 아래로 늘리고 패딩으로 밀어낸다
        height: 'calc(56px + env(safe-area-inset-bottom, 0px))',
        pb: 'env(safe-area-inset-bottom, 0px)',
        boxSizing: 'border-box',
        borderTop: isDark ? '1px solid #333' : '1px solid #eee',
        zIndex: 990,
        background: isDark ? '#1e1e1e' : '#fff',
        overflowX: 'auto',
        overflowY: 'hidden',
        flexWrap: 'nowrap',
        justifyContent: 'center',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      {navItems.map(item => (
        <BottomNavigationAction
          key={item.value}
          label={item.label}
          value={item.value}
          icon={item.icon}
          sx={{
            minWidth: '56px',
            padding: '6px 4px',
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.65rem',
              marginTop: '2px',
              whiteSpace: 'nowrap',
              // 선택 시 폰트가 커지며 줄바꿈되는 기본 동작 억제
              '&.Mui-selected': {
                fontSize: '0.65rem',
              },
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.3rem',
            },
          }}
        />
      ))}
    </BottomNavigation>
  );
}

export default BottomNavBar;